import { computed, reactive, watch } from 'vue';
import type { Id, ObjectLink, ObjectRecord } from '@vp/shared-types';
import { boxBounds, ulid } from '@vp/core';
import { live, ui } from './bridge';
import { reduceEffects } from '../theme';
import { t } from '../i18n';
import { useApp } from '../stores/app';

/**
 * Saved views of a canvas (bookmarks: a place and a zoom, kept with the canvas) and links on
 * objects — to a bookmark, another object, another canvas or a web address.
 */
export interface Bookmark {
  id: string;
  name: string;
  cx: number;
  cy: number;
  zoom: number;
}

const MAX_BOOKMARKS = 100;

function readList(v: unknown): Bookmark[] {
  if (!Array.isArray(v)) return [];
  return v.filter((b): b is Bookmark => !!b && typeof b === 'object' && typeof (b as Bookmark).id === 'string' && typeof (b as Bookmark).name === 'string' && [(b as Bookmark).cx, (b as Bookmark).cy, (b as Bookmark).zoom].every(Number.isFinite));
}

/** Bookmarks of the open canvas. */
export const bookmarks = computed<Bookmark[]>(() => {
  const app = useApp();
  const s = app.active ? app.sessions.get(app.active) : null;
  return readList(s?.meta.value.settings?.['bookmarks']);
});

async function save(list: Bookmark[]): Promise<void> {
  const app = useApp();
  if (app.active) await app.setCanvasSetting(app.active, 'bookmarks', list.length ? list : null);
}

export async function addBookmark(name?: string): Promise<Bookmark | null> {
  const cam = live.renderer?.camera;
  if (!cam) return null;
  const list = bookmarks.value;
  if (list.length >= MAX_BOOKMARKS) {
    useApp().notify('warning', t('bookmarks.tooMany', { n: MAX_BOOKMARKS }));
    return null;
  }
  const b: Bookmark = { id: ulid(), name: (name ?? '').trim().slice(0, 80) || t('bookmarks.defaultName', { n: list.length + 1 }), cx: cam.cx, cy: cam.cy, zoom: cam.zoom };
  await save([...list, b]);
  return b;
}

export async function renameBookmark(id: string, name: string): Promise<void> {
  const n = name.trim().slice(0, 80);
  if (n) await save(bookmarks.value.map((b) => (b.id === id ? { ...b, name: n } : b)));
}

export async function updateBookmarkView(id: string): Promise<void> {
  const cam = live.renderer?.camera;
  if (cam) await save(bookmarks.value.map((b) => (b.id === id ? { ...b, cx: cam.cx, cy: cam.cy, zoom: cam.zoom } : b)));
}

export async function removeBookmark(id: string): Promise<void> {
  await save(bookmarks.value.filter((b) => b.id !== id));
}

export async function moveBookmark(id: string, delta: -1 | 1): Promise<void> {
  const list = [...bookmarks.value];
  const i = list.findIndex((b) => b.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  await save(list);
}

let flight = 0;
/** Move the camera there smoothly (centre in a straight line, zoom evenly on a log scale). */
export function flyTo(target: { cx: number; cy: number; zoom: number }, ms = 380): void {
  const cam = live.renderer?.camera;
  if (!cam) return;
  cancelAnimationFrame(flight);
  const epoch = live.editor?.beginFlight(target) ?? 0;
  const from = { cx: cam.cx, cy: cam.cy, zoom: cam.zoom };
  const reduce = reduceEffects();
  const go = (k: number): boolean => {
    if ((live.editor?.viewEpoch ?? 0) !== epoch) return false;
    const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
    cam.set({ cx: from.cx + (target.cx - from.cx) * e, cy: from.cy + (target.cy - from.cy) * e, zoom: Math.exp(Math.log(from.zoom) + (Math.log(target.zoom) - Math.log(from.zoom)) * e) });
    return true;
  };
  if (reduce) {
    go(1);
    return;
  }
  const t0 = performance.now();
  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / ms);
    if (!go(k)) return;
    if (k < 1) flight = requestAnimationFrame(step);
  };
  flight = requestAnimationFrame(step);
}

export function goToBookmark(id: string): boolean {
  const b = bookmarks.value.find((x) => x.id === id);
  if (!b) return false;
  flyTo(b);
  return true;
}

// ---------- links ----------

/** Choosing the object a link leads to: the next object clicked (Esc / empty canvas cancels). */
export const linkPick = reactive({ from: null as Id | null });

export function setLink(id: Id, link: ObjectLink | null): void {
  live.editor?.updateObjects([{ id, patch: { link: link ?? undefined } as Partial<ObjectRecord> }], 'link');
}

/** Only http(s) and mail addresses are followed; anything else (javascript:, data:…) is refused. */
export function safeUrl(raw: string): string | null {
  const s = raw.trim();
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? 'mailto:' + s : 'https://' + s;
  try {
    const u = new URL(withScheme);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:' ? u.href : null;
  } catch {
    return null;
  }
}

/** A short text for the link: the bookmark / object / canvas name or the address. */
export function linkLabel(link: ObjectLink): string {
  const app = useApp();
  switch (link.kind) {
    case 'bookmark':
      return bookmarks.value.find((b) => b.id === link.target)?.name ?? t('links.missing');
    case 'object': {
      const o = live.editor?.doc.get(link.target);
      return o ? o.name || String(o.props['text'] ?? o.props['title'] ?? '').slice(0, 40) || t('links.object') : t('links.missing');
    }
    case 'canvas':
      return app.canvases.find((c) => c.id === link.target)?.name ?? t('links.missing');
    case 'url':
      return link.target.replace(/^https?:\/\//, '').slice(0, 60);
  }
}

export async function followLink(link: ObjectLink): Promise<void> {
  const app = useApp();
  switch (link.kind) {
    case 'bookmark':
      if (!goToBookmark(link.target)) app.notify('warning', t('links.brokenBookmark'));
      return;
    case 'object': {
      const ed = live.editor;
      const o = ed?.doc.get(link.target);
      const cam = live.renderer?.camera;
      if (!ed || !o || !cam) return void app.notify('warning', t('links.brokenObject'));
      ed.select([o.id]);
      const b = boxBounds(o);
      const zoom = Math.min(Math.max(cam.zoom, 0.25), Math.min(cam.width / (b.w + 200), cam.height / (b.h + 200)));
      flyTo({ cx: b.x + b.w / 2, cy: b.y + b.h / 2, zoom: Math.min(zoom, Math.max(cam.zoom, 1)) });
      return;
    }
    case 'canvas':
      if (!app.canvases.some((c) => c.id === link.target)) return void app.notify('warning', t('links.brokenCanvas'));
      await app.openCanvas(link.target);
      return;
    case 'url': {
      const url = safeUrl(link.target);
      if (!url) return void app.notify('warning', t('links.badUrl'));
      // a new tab that cannot reach back to the app
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}

/** Picking the target object: called when the selection changes while a pick is on. */
export function finishLinkPick(selection: Id[]): void {
  const from = linkPick.from;
  if (!from) return;
  if (!selection.length || (selection.length === 1 && selection[0] === from)) {
    if (!selection.length) linkPick.from = null;
    return;
  }
  linkPick.from = null;
  setLink(from, { kind: 'object', target: selection[0] });
  live.editor?.select([from]);
  useApp().notify('info', t('links.set'), 2000);
}

watch(
  () => ui.selection,
  (sel) => finishLinkPick(sel),
);
